/**
 * Client-side PKCE + state helpers for the Google hosted-UI authorize redirect.
 *
 * The browser generates a code verifier, its S256 challenge, and an opaque
 * state token; the verifier and state are persisted in sessionStorage under the
 * keys exported here so the callback route can complete the exchange and detect
 * CSRF. Server-side session binding arrives with a later auth-harness rework.
 */

/** sessionStorage key holding the opaque state token across the redirect. */
export const OAUTH_STATE_STORAGE_KEY = 'belhop_oauth_state';
/** sessionStorage key holding the PKCE code verifier across the redirect. */
export const OAUTH_CODE_VERIFIER_STORAGE_KEY = 'belhop_oauth_code_verifier';

const CODE_VERIFIER_BYTE_LENGTH = 32;
const STATE_BYTE_LENGTH = 16;

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomBase64Url(byteLength: number): string {
  const randomBytes = new Uint8Array(byteLength);
  crypto.getRandomValues(randomBytes);
  return base64UrlEncode(randomBytes);
}

/** Fresh high-entropy PKCE code verifier (base64url, 43 chars for 32 bytes). */
export function generateCodeVerifier(): string {
  return randomBase64Url(CODE_VERIFIER_BYTE_LENGTH);
}

/** S256 code challenge: base64url(SHA-256(codeVerifier)) per RFC 7636. */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoded = new TextEncoder().encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return base64UrlEncode(new Uint8Array(digest));
}

/** Opaque anti-CSRF state token echoed back on the callback URL. */
export function generateState(): string {
  return randomBase64Url(STATE_BYTE_LENGTH);
}
